import type { ZodType } from 'zod';
import {
  type RecordEventBody,
  RecordEventResponse,
  MeNotificationsResponse,
  UpdateNotificationsBody,
  MeLocaleResponse,
  UpdateLocaleBody,
  CreateAppealResponse,
  AdminStatusResponse,
  OverviewResponse,
  KFactorResponse,
  QuestionAnswerView,
  BoxPublicView,
  InboxResponse,
  RecordAdSourceResponse,
  type AdminStatusResponse as AdminStatusResponseType,
  type OverviewResponse as OverviewResponseType,
  type KFactorResponse as KFactorResponseType,
  type QuestionAnswerView as QuestionAnswerViewType,
  type BoxPublicView as BoxPublicViewType,
  type InboxResponse as InboxResponseType,
  type BoxFormat,
  type Locale,
  getStrings,
} from '@veylme/shared';

import { env } from '../env.js';
import { getRawInitData } from '../lib/tg.js';
import { notifyAuthFailure } from '../lib/sessionExpiry.js';
import { detectLocale } from '../i18n/detectLocale.js';
import { ApiAuthError, ApiError, ApiParseError, ApiRateLimitError } from './errors.js';
import type { AuthFailureReason } from './errors.js';

/**
 * Опции для `apiFetch`.
 *
 * CANON D1: `body` передаётся как есть — вызывающий НЕ должен класть туда
 * `user_id`. Сервер достаёт пользователя из initData в `Authorization` header.
 *
 * CANON D2: `parse` — обязательная zod-схема, response парсится через неё
 * перед возвратом. Нельзя доверять прозе JSON от API.
 */
export interface ApiFetchOptions<T> {
  method?: 'GET' | 'POST' | 'PATCH';
  body?: unknown;
  parse: ZodType<T>;
  query?: Record<string, string>;
  /**
   * Не сообщать о 401 глобальному перехвату (`lib/sessionExpiry.ts`) — ошибка
   * всё равно бросается вызывающему, но полноэкранный экран не показывается.
   *
   * Нужен ровно для аналитики: инвариант «аналитика НИКОГДА не ломает UI»
   * (см. докстринг `emitEvent`, threat-model R.b). Без флага 401 на фоновом
   * `POST /events` погасил бы весь интерфейс живому пользователю.
   */
  silentAuth?: boolean;
}

/**
 * Реальный контракт API: `{ error: '<строка-код>' }`.
 * Например: { error: 'moderation_unavailable' }, { error: 'bot_api_unavailable' }.
 * НЕ объект { code, message } — именно строка.
 *
 * У 401 к этому добавляется ВТОРОЕ плоское поле `code` с причиной отказа
 * initData: `{ error: 'unauthorized', code: 'stale' }`
 * (`apps/api/src/plugins/auth.ts:41,54`).
 */
interface ApiErrorResponse {
  error?: string;
  /** Только у 401: причина отказа initData (`apps/api/src/plugins/auth.ts:54`). */
  code?: string;
}

function isApiErrorResponse(value: unknown): value is ApiErrorResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as Record<string, unknown>).error === 'string'
  );
}

/** Причины отказа, которые сервер реально присылает (`api/errors.ts`). */
const KNOWN_AUTH_REASONS: readonly AuthFailureReason[] = [
  'stale',
  'hmac',
  'missing',
  'malformed',
  'user-parse',
];

/**
 * Достаёт причину отказа из плоского `code` тела 401. Всё незнакомое (нет поля,
 * не строка, значение из будущей версии сервера) → `'unknown'`.
 * Чистая, никогда не бросает (CANON G3) — вызывается на пути обработки ошибки.
 */
function readAuthReason(body: unknown): AuthFailureReason {
  if (typeof body !== 'object' || body === null) return 'unknown';
  const raw = (body as Record<string, unknown>).code;
  if (typeof raw !== 'string') return 'unknown';
  return KNOWN_AUTH_REASONS.find((r) => r === raw) ?? 'unknown';
}

function buildUrl(path: string, query?: Record<string, string>): string {
  const base = env.VITE_API_BASE_URL.replace(/\/$/, '');
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query || Object.keys(query).length === 0) return url;
  const qs = new URLSearchParams(query).toString();
  return `${url}?${qs}`;
}

/**
 * Универсальный fetch к `@veylme/api`.
 *
 * - Добавляет `Authorization: tma <raw initData>` из `getRawInitData()`.
 * - Добавляет `Content-Type: application/json` только если `body` есть.
 * - Сериализует `body` через `JSON.stringify` (без user_id — CANON D1).
 * - Маппит статусы на типизированные ошибки (см. `./errors.ts`).
 * - На 2xx парсит response через `opts.parse` (CANON D2).
 */
export async function apiFetch<T>(
  path: string,
  opts: ApiFetchOptions<T>,
): Promise<T> {
  const url = buildUrl(path, opts.query);
  const initDataRaw = getRawInitData();

  // CANON SP3: лучший UX — не делать запрос с пустым токеном, сразу показать
  // «перезапустите Mini App». Иначе сервер ответит 401 и пользователь увидит
  // ту же ошибку, но с лишним сетевым раундтрипом.
  if (initDataRaw === undefined || initDataRaw === '') {
    if (opts.silentAuth !== true) notifyAuthFailure('no-initdata');
    throw new ApiAuthError(
      getStrings(detectLocale()).err_not_in_telegram,
      undefined,
      'no-initdata',
    );
  }

  const headers: Record<string, string> = {
    Authorization: `tma ${initDataRaw}`,
  };

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
  };

  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.body);
  }

  const response = await fetch(url, init);

  if (!response.ok) {
    let code: string | undefined;
    // Причина отказа initData — только у 401; на прочих статусах остаётся 'unknown'.
    let authReason: AuthFailureReason = 'unknown';
    // message — технический, только для логов. UI НЕ показывает err.message напрямую.
    const message = `HTTP ${response.status}`;
    try {
      const errBody: unknown = await response.json();
      if (isApiErrorResponse(errBody) && errBody.error) {
        // API отдаёт `{ error: '<строка-код>' }` — берём строку как code.
        code = errBody.error;
      }
      authReason = readAuthReason(errBody);
    } catch {
      // Body не JSON или пустой — code остаётся undefined, причина 'unknown'.
    }

    if (response.status === 401) {
      if (opts.silentAuth !== true) notifyAuthFailure(authReason);
      throw new ApiAuthError(message, code, authReason);
    }
    if (response.status === 429) throw new ApiRateLimitError(message, code);
    throw new ApiError(response.status, message, code);
  }

  const json: unknown = await response.json();
  // Отличаем несоответствие схемы (ApiParseError) от сетевой ошибки (TypeError).
  // Так humanizeApiError может показать правильное сообщение пользователю.
  try {
    return opts.parse.parse(json);
  } catch (cause) {
    throw new ApiParseError(
      `Response schema mismatch on ${opts.method ?? 'GET'} ${path}`,
      cause,
    );
  }
}

/**
 * Тонкая обёртка `POST /events` для клиентских событий виральной петли (Веха 8).
 *
 * Analytics fail-soft (CANON E-инвариант / threat-model R.b): аналитика НИКОГДА
 * не ломает UI — все ошибки (сеть / 4xx / 5xx / отсутствие initData) глотаются.
 * Fire-and-forget: caller зовёт `void emitEvent(...)` в onClick после share-
 * результата или в mount-эффекте (ask_opened) и НЕ ждёт результат.
 *
 * `silentAuth: true` — часть того же инварианта: 401 отсюда НЕ поднимает
 * глобальный экран «пропуск устарел» (`lib/sessionExpiry.ts`). Иначе фоновая
 * аналитика гасила бы UI живому пользователю. Настоящий отказ всё равно
 * приедет с первого же продуктового запроса.
 *
 * CANON D1: body НЕ содержит user_id — сервер берёт identity из initData.
 */
export async function emitEvent(body: RecordEventBody): Promise<void> {
  try {
    await apiFetch('/events', {
      method: 'POST',
      body,
      parse: RecordEventResponse,
      silentAuth: true,
    });
  } catch {
    // swallow — analytics best-effort.
  }
}

/**
 * GET /me/notifications — получить текущий флаг уведомлений пользователя.
 * Возвращает { enabled: boolean }.
 */
export async function getNotifications(): Promise<{ enabled: boolean }> {
  return apiFetch('/me/notifications', { parse: MeNotificationsResponse });
}

/**
 * PATCH /me/notifications — установить флаг уведомлений.
 * Принимает { enabled: boolean }, возвращает { enabled: boolean }.
 */
export async function updateNotifications(enabled: boolean): Promise<{ enabled: boolean }> {
  return apiFetch('/me/notifications', {
    method: 'PATCH',
    body: UpdateNotificationsBody.parse({ enabled }),
    parse: MeNotificationsResponse,
  });
}

/**
 * GET /me/locale — получить сохранённый язык пользователя.
 * Возвращает { locale: 'ru' | 'en' | null }. null = не задан, брать detectLocale.
 */
export async function getLocalePref(): Promise<{ locale: Locale | null }> {
  return apiFetch('/me/locale', { method: 'GET', parse: MeLocaleResponse });
}

/**
 * PATCH /me/locale — сохранить выбранный язык.
 * Принимает locale: 'ru' | 'en', возвращает { locale: 'ru' | 'en' | null }.
 */
export async function updateLocalePref(locale: Locale): Promise<{ locale: Locale | null }> {
  return apiFetch('/me/locale', {
    method: 'PATCH',
    body: UpdateLocaleBody.parse({ locale }),
    parse: MeLocaleResponse,
  });
}

/**
 * GET /me/admin — статус администратора текущего пользователя.
 * Возвращает { isAdmin: boolean }.
 */
export async function getAdminStatus(): Promise<AdminStatusResponseType> {
  return apiFetch('/me/admin', { parse: AdminStatusResponse });
}

/**
 * GET /analytics/overview — сводка посещаемости и активности (admin-only).
 * Возвращает OverviewResponse.
 */
export async function getAnalyticsOverview(): Promise<OverviewResponseType> {
  return apiFetch('/analytics/overview', { parse: OverviewResponse });
}

/**
 * GET /analytics/k — K-фактор виральности за период (admin-only, work-028 A).
 * Возвращает KFactorResponse. `days` — окно агрегации (1..365, default 30).
 */
export async function getAnalyticsK(days = 30): Promise<KFactorResponseType> {
  return apiFetch('/analytics/k', {
    query: { days: String(days) },
    parse: KFactorResponse,
  });
}

/**
 * POST /senders/appeal — подать апелляцию на общий бан (work-024 C, ADR-0012).
 *
 * fingerprint резолвится серверно из initData — клиент НЕ передаёт его (CANON B4/SP4).
 * message опционален: в v1 шлём пустую апелляцию, admin видит факт подачи.
 * Идемпотентно: повторный POST при уже открытой pending-апелляции не создаёт дубль.
 */
export async function submitAppeal(message?: string): Promise<{ ok: true }> {
  return apiFetch('/senders/appeal', {
    method: 'POST',
    body: message !== undefined ? { message } : {},
    parse: CreateAppealResponse,
  });
}

/**
 * GET /questions/:id/answer — вид ответа для анонимного спрашивающего
 * (security/threat-models/answer-view.md). Авторизация = обладание
 * непредсказуемым `id` (localStorage на устройстве спрашивающего), НЕ ownership.
 * `{ answered:false, answer:null }` — единообразно для «ещё не ответили» и любого
 * иного случая (anti-oracle) — вызывающий код трактует как «ждём ответа».
 */
export async function getQuestionAnswer(id: string): Promise<QuestionAnswerViewType> {
  return apiFetch(`/questions/${encodeURIComponent(id)}/answer`, {
    parse: QuestionAnswerView,
  });
}

/**
 * GET /inbox — страница входящих (work-036 Трек D). `format` опционален:
 * без него сервер отдаёт обычный ask-инбокс (текущее поведение InboxScreen);
 * `format:'message'` — сообщения message-ящика (тот же InboxResponse shape).
 * `cursor`/`limit` — курсорная пагинация (см. `InboxQuery` на сервере).
 */
export async function getInbox(
  params: { cursor?: string; limit?: number; format?: BoxFormat } = {},
): Promise<InboxResponseType> {
  const query: Record<string, string> = {};
  if (params.cursor !== undefined) query.cursor = params.cursor;
  if (params.limit !== undefined) query.limit = String(params.limit);
  if (params.format !== undefined) query.format = params.format;
  return apiFetch('/inbox', { parse: InboxResponse, query });
}

/**
 * GET /boxes/link/:code — резолв короткого кода ссылки (work-035, §4) в
 * ПОЛНЫЙ `BoxPublicView` (с `token`). Вызывается ОДИН раз на bootstrap
 * (`App.tsx`, статус `ASK_RESOLVING`) — дальше весь ASK-поток (`/questions`,
 * `/events`, `ref.sourceBoxToken`) идёт с ПОЛНЫМ токеном, как раньше.
 * `requireAuth` на сервере — `apiFetch` и так всегда шлёт `Authorization: tma`.
 */
export async function getBoxByLinkCode(code: string): Promise<BoxPublicViewType> {
  return apiFetch(`/boxes/link/${encodeURIComponent(code)}`, { parse: BoxPublicView });
}

/**
 * POST /users/ad-source — рекламная first-touch атрибуция, path 2 (мини-апп
 * открыт напрямую по `?startapp=a<code>`, work-035 §6). Fail-soft на стороне
 * caller (`App.tsx`) — ошибка НЕ должна блокировать онбординг.
 */
export async function postAdSource(code: string): Promise<void> {
  await apiFetch('/users/ad-source', {
    method: 'POST',
    body: { code },
    parse: RecordAdSourceResponse,
  });
}
